import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/mine");
      setItems(data.rows || []);
      setUnread(data.unread || 0);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        aria-label="Notifications"
        className="p-2 rounded-sm hover:bg-ec-mute relative"
        onClick={() => setOpen((v) => !v)}
        data-testid="navbar-alerts-button"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold bg-ec-emerald text-white rounded-full flex items-center justify-center" data-testid="alerts-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[92vw] bg-white border border-ec-line shadow-xl rounded-sm overflow-hidden z-50" data-testid="notifications-popover">
          <div className="flex items-center justify-between px-4 py-3 border-b border-ec-line">
            <span className="font-semibold text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-ec-emerald flex items-center gap-1 hover:underline" data-testid="notifications-mark-all">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[28rem] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-ec-ink-soft" data-testid="notifications-empty">
                You're all caught up.
              </div>
            )}
            {items.map((n) => (
              <Link
                to={n.link || "#"}
                key={n.id}
                onClick={() => { setOpen(false); api.post(`/notifications/${n.id}/read`).catch(() => {}); }}
                className={`block px-4 py-3 border-b border-ec-line hover:bg-ec-mute transition-colors ${!n.read ? "bg-ec-emerald/5" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="text-sm font-semibold flex items-center gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 bg-ec-emerald rounded-full" />}
                  {n.title}
                </div>
                <div className="text-xs text-ec-ink-soft mt-1 leading-snug">{n.body}</div>
                <div className="text-[10px] font-mono-plex text-ec-ink-soft mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
